import type { Call, Device } from "@twilio/voice-sdk";
import type { AudioChoice } from "./audio-devices";
import {
  browserPhoneErrorOf,
  BrowserPhoneLoadError,
  phoneErrorDetailOf,
  type BrowserCall,
  type BrowserCallEvents,
  type BrowserPhone,
} from "./browser-phone";

// Il telefono del browser con il Voice SDK di Twilio (piano M4, D10), dalla pagina «Prova chiamate» di R1. Il SDK pesa
// centinaia di kB, quindi si carica con `import()` e resta fuori dal pacchetto principale (la velocità è un criterio di
// accettazione); ma non aspetta la prima chiamata: `preload` lo scarica in sottofondo appena il guscio di chi chiama è pronto
// (prova del Power Dialer del 6 ottobre 2026). Una pagina aperta prima di un aggiornamento ha così già il SDK in memoria, e la
// sua prima chiamata parte; scaricato soltanto alla chiamata, il file avrebbe già cambiato nome sul server. Il caricamento è
// uno solo per pagina (`loading`), e uno fallito si riprova alla chiamata. Ogni chiamata ha il suo `Device`, creato con il
// token dato e distrutto quando la chiamata finisce; il token lo chiede (e lo riusa finché vale, D10) chi chiama, mai questo
// modulo.
// Il microfono e l'ascolto scelti nella postazione (richiesta del product owner del 6 ottobre 2026, `audio-devices.ts`) vanno
// sul `Device` prima di comporre, come consiglia il SDK 2.18.5 (`device.audio.setInputDevice`, poi `device.connect`), e
// l'ascolto soltanto dove il browser sa sceglierlo (`isOutputSelectionSupported`). Durante la chiamata la scelta nuova passa
// alla chiamata, e il dispositivo scelto che arriva o torna (cuffie collegate tardi, Bluetooth che si riconnette, un elenco
// dei dispositivi più lento dell'attesa) la riprende da sé. Un dispositivo che manca o che non risponde non ferma mai la
// chiamata: resta quello di sistema. Il `Device` distrutto a fine chiamata libera anche il microfono scelto.
// Ogni microfono della chiamata ha accesi i filtri del browser contro l'eco, il rumore e il volume che varia
// (`VOICE_FILTERS`, prova del 6 ottobre 2026: il rumore di fondo era più forte che in Close); il SDK li dà con
// `setAudioConstraints` ai microfoni che apre `setInputDevice`, e con `rtcConstraints` di `connect` a quello di sistema che
// la chiamata apre da sé. Tre casi rari rimasti dalla #127: (a) un cambio durante la chiamata aspetta lo squillo o la
// risposta, perché mentre la chiamata apre il suo primo microfono un `setInputDevice` si incrocerebbe con quel passo del SDK;
// (b) un cambio rifiutato durante la chiamata riprova una volta con il microfono di sistema, perché il SDK chiude quello
// iniziale della chiamata prima di aprire il nuovo (su Safari e Firefox la chiamata resterebbe muta); (c) ogni passo della
// coda dei cambi ha 5 secondi al massimo, poi la coda va avanti, così una richiesta del microfono che non risponde mai non
// blocca i cambi dopo (lei resta com'è, senza errori); se risponde tardi, la scelta di adesso si rimette sopra di lei.

/** Del SDK serve soltanto `Device`. */
type VoiceSdk = { Device: typeof Device };
const importVoiceSdk = (): Promise<VoiceSdk> => import("@twilio/voice-sdk");
let importer: () => Promise<VoiceSdk> = importVoiceSdk;
let loading: Promise<VoiceSdk> | null = null;

function loadVoiceSdk(): Promise<VoiceSdk> {
  // Un caricamento fallito (rete) si riprova alla chiamata dopo.
  loading ??= importer().catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

/** Soltanto per i test: un importatore finto del SDK (`null` rimette quello vero), con il caricamento in memoria azzerato. */
export function setVoiceSdkImporterForTests(next: (() => Promise<VoiceSdk>) | null): void {
  importer = next ?? importVoiceSdk;
  loading = null;
}

/** Il gestore dell'audio del `Device` (`AudioHelper` del SDK). */
type DeviceAudio = NonNullable<Device["audio"]>;

/** I dispositivi da mettere sul `Device`: un `deviceId`, `null` per quello di sistema, `undefined` per lasciarlo com'è. */
type WantedDevices = { input?: string | null; output?: string | null };

/** Quanto al massimo si aspetta che il `Device` conosca i dispositivi del browser: poi si compone comunque. */
const DEVICE_LIST_WAIT_MS = 1_000;

/**
 * Il filtro del browser acceso per scelta (prova del 6 ottobre 2026, questione 217): Chrome lo accende di suo, ma niente lo
 * garantiva, nemmeno dopo `setInputDevice`. Valori semplici, cioè preferenze: un browser che non ne sa uno lo ignora, e il
 * microfono si apre lo stesso. Krisp no: nessun filtro a pagamento (product owner, 9 ottobre 2026).
 */
const VOICE_FILTERS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true } as const;

/** Quanto al massimo la coda dei cambi aspetta un passo: poi va avanti, e il passo resta com'è. */
const SWITCH_STEP_LIMIT_MS = 5_000;

/**
 * Le richieste del microfono del SDK rimaste senza risposta oltre il limite di un passo: `sdkInputSettled` non le aspetta
 * più, altrimenti il passo dopo resterebbe fermo su di loro.
 */
const unanswered = new WeakSet<Promise<void>>();

/**
 * Un giro del ciclo degli eventi, per lasciar finire ciò che il SDK fa da sé quando i dispositivi cambiano: rimette
 * l'ascolto su «default» (subito e una microtask dopo, appena letti i dispositivi) e, con il microfono di sistema in uso,
 * lo riapre in un `setTimeout(0)`. Così la scelta arriva dopo e resta.
 */
function afterSdkReaction(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Aspetta che il `Device` abbia letto i dispositivi del browser: appena nato non ne conosce nessuno, e `setInputDevice` o
 * `speakerDevices.set` rifiuterebbero ogni `deviceId`. Il SDK lo dice con `deviceChange`; poi un giro, `afterSdkReaction`.
 * Prima di comporre, con la 2.18.5, la scelta arriverebbe dopo i due «default» del SDK anche senza il giro, ma per una
 * microtask sola: il giro la tiene dopo anche se quell'ordine cambia, e durante la chiamata lascia partire prima la
 * reazione del SDK a un cambio appena avvenuto, che `sdkInputSettled` poi aspetta.
 */
async function deviceListRead(audio: DeviceAudio): Promise<void> {
  if (audio.availableInputDevices.size === 0 && audio.availableOutputDevices.size === 0) {
    await new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer);
        audio.removeListener("deviceChange", done);
        resolve();
      };
      const timer = setTimeout(done, DEVICE_LIST_WAIT_MS);
      audio.on("deviceChange", done);
    });
  }
  await afterSdkReaction();
}

/**
 * Aspetta che finisca il cambio di microfono che il SDK sta già facendo da sé (per esempio la riapertura di «default» dopo
 * un cambio dei dispositivi): un `getUserMedia` nostro partito insieme potrebbe finire prima del suo, e il suo vincerebbe.
 * `_getInputDevicePromise` è la promessa che il `Device` stesso aspetta prima di comporre (dichiarata nei tipi della 2.18.5);
 * se nel frattempo ne parte un'altra si aspetta anche quella, per pochi giri al massimo. Una richiesta già rimasta senza
 * risposta oltre il limite di un passo (`unanswered`) non si aspetta.
 */
async function sdkInputSettled(audio: DeviceAudio): Promise<void> {
  for (let round = 0; round < 3; round += 1) {
    const pending = audio._getInputDevicePromise();
    if (pending === null || unanswered.has(pending)) return;
    await pending.catch(() => undefined);
  }
}

/** La voce di sistema: «default» (Chrome), altrimenti il primo dispositivo, che è quello di sistema (Safari). */
function systemEntry(available: ReadonlyMap<string, MediaDeviceInfo>): string | null {
  if (available.has("default")) return "default";
  return available.keys().next().value ?? null;
}

/** Se la chiamata suona soltanto sul dispositivo dato. */
function playsOn(audio: DeviceAudio, deviceId: string): boolean {
  const active = [...audio.speakerDevices.get()];
  return active.length === 1 && active[0]?.deviceId === deviceId;
}

/**
 * Ciò che della scelta manca sul `Device` e si può mettere adesso: il dispositivo scelto è fra quelli collegati e non è
 * quello in uso. Quello di sistema (`null`) lo segue il SDK da sé.
 */
function missingFromDevice(audio: DeviceAudio, choice: AudioChoice): WantedDevices {
  const { input, output } = choice;
  const inputBack = input !== null && audio.availableInputDevices.has(input) && audio.inputDevice?.deviceId !== input;
  const outputBack = output !== null && audio.isOutputSelectionSupported && audio.availableOutputDevices.has(output) && !playsOn(audio, output);
  return { ...(inputBack ? { input } : {}), ...(outputBack ? { output } : {}) };
}

/**
 * Come `missingFromDevice`, ma anche per la scelta di sistema (`null`): il `Device` usa un microfono che non è quello di
 * sistema (il flusso iniziale della chiamata, `inputDevice` vuoto, lo è), o suona altrove. Serve dopo un passo tagliato dal
 * limite che ha risposto tardi, e ha messo sul `Device` una scelta ormai vecchia: il SDK fa vincere l'ultimo microfono che
 * risponde, non l'ultimo chiesto.
 */
function divergentFrom(audio: DeviceAudio, choice: AudioChoice): WantedDevices {
  const systemInput = systemEntry(audio.availableInputDevices);
  const systemOutput = systemEntry(audio.availableOutputDevices);
  const inputOff = choice.input === null && systemInput !== null && audio.inputDevice !== null && audio.inputDevice.deviceId !== systemInput;
  const outputOff = choice.output === null && audio.isOutputSelectionSupported && systemOutput !== null && !playsOn(audio, systemOutput);
  return { ...missingFromDevice(audio, choice), ...(inputOff ? { input: null } : {}), ...(outputOff ? { output: null } : {}) };
}

/**
 * Mette sul `Device` i dispositivi dati. Uno che manca o che non risponde lascia quello di prima, senza errori: la chiamata
 * non si ferma mai per il microfono. Durante la chiamata (`inCall`) un microfono rifiutato riprova una volta con quello di
 * sistema: il SDK, prima di aprire il nuovo, ha già chiuso il microfono iniziale della chiamata.
 */
async function applyDevices(device: Device, audio: DeviceAudio, wanted: WantedDevices, inCall: boolean): Promise<void> {
  if (wanted.input !== undefined) {
    await sdkInputSettled(audio);
    if (device.audio !== audio) return;
    const id = wanted.input ?? systemEntry(audio.availableInputDevices);
    const before = audio.inputDevice;
    try {
      if (id !== null) await audio.setInputDevice(id);
    } catch {
      // Scollegato o occupato. Prima di comporre resta quello di sistema, che la chiamata apre da sé. Durante la chiamata
      // si riprova con quello di sistema, salvo che intanto il microfono sia cambiato (un passo dopo, o il SDK da sé):
      // allora la chiamata ne ha già uno acceso.
      const system = systemEntry(audio.availableInputDevices);
      if (inCall && system !== null && device.audio === audio && audio.inputDevice === before) {
        await audio.setInputDevice(system).catch(() => undefined);
      }
    }
    if (device.audio !== audio) {
      // La chiamata è finita mentre il microfono nuovo si apriva: il SDK lo ha tenuto, acceso, sul `Device` già distrutto.
      for (const track of audio.inputStream?.getTracks() ?? []) track.stop();
      return;
    }
  }
  if (wanted.output !== undefined && audio.isOutputSelectionSupported) {
    const id = wanted.output ?? systemEntry(audio.availableOutputDevices);
    try {
      if (id !== null) await audio.speakerDevices.set(id);
    } catch {
      // Scollegato: resta l'ascolto di prima.
    }
  }
}

/** Come `applyDevices`, aspettando prima che il `Device` conosca i dispositivi del browser. */
async function setDevices(device: Device, wanted: WantedDevices, inCall: boolean): Promise<void> {
  const audio = device.audio;
  if (!audio || (wanted.input === undefined && wanted.output === undefined)) return;
  await deviceListRead(audio);
  // La chiamata finita nel frattempo: il `Device` distrutto non ha più `audio`.
  if (device.audio !== audio) return;
  await applyDevices(device, audio, wanted, inCall);
}

/**
 * Compone con il token dato e i parametri della chiamata (per le chiamate dalla scheda soltanto `callId`), dopo aver messo
 * sul `Device` i filtri del browser, il microfono e l'ascolto scelti. Risolve appena il SDK ha creato la chiamata, prima che
 * squilli: da lì in poi parlano gli eventi. Rifiuta se il SDK non si carica o non crea la chiamata. Il `Device` si distrugge
 * una volta sola, alla fine della chiamata, a un suo errore o al riaggancio.
 */
async function startBrowserCall(input: {
  token: string;
  params: Readonly<Record<string, string>>;
  events: BrowserCallEvents;
  audio: AudioChoice;
}): Promise<BrowserCall> {
  let sdk: VoiceSdk;
  try {
    sdk = await loadVoiceSdk();
  } catch (error) {
    throw new BrowserPhoneLoadError(error);
  }
  const { events } = input;
  const device: Device = new sdk.Device(input.token);
  // Senza `audio` nel SDK finto delle prove end-to-end.
  const audio: DeviceAudio | null = device.audio ?? null;
  // I filtri per ogni microfono che `setInputDevice` aprirà; sul `Device` appena nato non ce n'è ancora uno da riaprire, e
  // un rifiuto lascia quelli del browser.
  void audio?.setAudioConstraints(VOICE_FILTERS).catch(() => undefined);
  let destroyed = false;
  let audioNow = input.audio;
  // La chiamata ha squillato o risposto, oppure è finita: da lì il suo primo microfono è aperto e i cambi possono partire.
  let heard: () => void = () => undefined;
  const callHeard = new Promise<void>((resolve) => {
    heard = resolve;
  });
  // Rimette sul `Device` la scelta di adesso dove non c'è, anche quella di sistema: dopo un passo tagliato dal limite.
  const realign = async () => {
    if (destroyed || !audio) return;
    await sdkInputSettled(audio);
    if (destroyed || device.audio !== audio) return;
    await applyDevices(device, audio, divergentFrom(audio, audioNow), true);
  };
  // I cambi di dispositivo uno alla volta, nell'ordine in cui arrivano: due scelte rapide finiscono sull'ultima. Un cambio può
  // aspettare `ready` (fuori dal limite), poi ha `SWITCH_STEP_LIMIT_MS`: oltre, la coda va avanti e il passo resta com'è; la
  // richiesta del microfono che lo tiene fermo non la aspetta più nessuno. Se poi il passo finisce (una risposta tardiva, per
  // esempio cuffie Bluetooth che si aprono in 7 secondi), il SDK lo ha messo sul `Device` sopra la scelta fatta dopo: in coda
  // si rimette la scelta di adesso (`realign`).
  let turn: Promise<void> = Promise.resolve();
  const withinLimit = (step: () => Promise<void>): Promise<void> => {
    const running = step();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const limit = new Promise<void>((resolve) => {
      timer = setTimeout(() => {
        const pending = audio?._getInputDevicePromise() ?? null;
        if (pending !== null) unanswered.add(pending);
        void running
          .catch(() => undefined)
          .then(() => {
            if (!destroyed) inTurn(realign, callHeard);
          });
        resolve();
      }, SWITCH_STEP_LIMIT_MS);
    });
    return Promise.race([running, limit]).finally(() => clearTimeout(timer));
  };
  const inTurn = (step: () => Promise<void>, ready?: Promise<void>) => {
    turn = turn
      .then(() => ready)
      .then(() => withinLimit(step))
      .catch(() => undefined);
  };
  // Il dispositivo scelto che arriva o torna la riprende, anche a chiamata in corso: a ogni `deviceChange`, dopo ciò che il
  // SDK fa da sé per lo stesso cambio (un giro solo per tutti gli avvisi della stessa lettura), si rimette ciò che manca. Un
  // dispositivo che non risponde lascia quello di prima: la chiamata non si ferma e non cade.
  let followTimer: ReturnType<typeof setTimeout> | undefined;
  const rejoin = async () => {
    if (destroyed || !audio) return;
    await sdkInputSettled(audio);
    if (destroyed || device.audio !== audio) return;
    await applyDevices(device, audio, missingFromDevice(audio, audioNow), true);
  };
  const follow = () => {
    clearTimeout(followTimer);
    followTimer = setTimeout(() => inTurn(rejoin), 0);
  };
  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    heard();
    clearTimeout(followTimer);
    audio?.removeListener("deviceChange", follow);
    device.destroy();
  };
  device.on("error", (error: unknown) => {
    events.onError(browserPhoneErrorOf(error), phoneErrorDetailOf(error, "device"));
    destroy();
  });
  audio?.on("deviceChange", follow);
  // Prima di comporre, soltanto ciò che è stato scelto: senza scelta il SDK usa i dispositivi di sistema. Primo passo della
  // coda, così un `deviceChange` che arriva intanto (anche dopo il secondo d'attesa) si segue dopo; ma senza il limite: il
  // SDK, prima di comporre, aspetta comunque la richiesta del microfono in corso, e se quella rifiutasse dopo il limite
  // rifiuterebbe anche `connect`, mentre aspettandola qui la chiamata parte con il microfono di sistema.
  const chosen: WantedDevices = { input: input.audio.input ?? undefined, output: input.audio.output ?? undefined };
  turn = setDevices(device, chosen, false).catch(() => undefined);
  await turn;
  let call: Call;
  try {
    // Senza un microfono scelto la chiamata apre quello di sistema con `rtcConstraints.audio`, non con i vincoli di
    // `setAudioConstraints`: i filtri vanno anche lì.
    call = await device.connect({ params: { ...input.params }, rtcConstraints: { audio: VOICE_FILTERS } });
  } catch (error) {
    destroy();
    throw error;
  }
  call.on("ringing", () => {
    heard();
    events.onRinging();
  });
  call.on("accept", () => {
    heard();
    events.onAccepted();
  });
  call.on("error", (error: unknown) => events.onError(browserPhoneErrorOf(error), phoneErrorDetailOf(error, "call")));
  const closed = () => {
    events.onClosed();
    destroy();
  };
  call.on("disconnect", closed);
  call.on("cancel", closed);
  call.on("reject", closed);
  return {
    hangUp() {
      call.disconnect();
      destroy();
    },
    switchAudio(choice) {
      if (destroyed) return;
      // Soltanto ciò che cambia. Il SDK lascia com'è un microfono scelto rimesso uguale, ma con `null` riaprirebbe come
      // «default» il microfono di sistema che la chiamata usa già, e l'ascolto rimesso uguale rifarebbe `setSinkId`.
      const wanted = {
        ...(choice.input === audioNow.input ? {} : { input: choice.input }),
        ...(choice.output === audioNow.output ? {} : { output: choice.output }),
      };
      audioNow = choice;
      // Dopo lo squillo o la risposta (`callHeard`), e i passi già in coda.
      inTurn(() => setDevices(device, wanted, true), callHeard);
    },
  };
}

/** Il telefono del browser dell'applicazione: soltanto Twilio ha oggi un telefono del browser. */
export const twilioPhone: BrowserPhone = {
  call({ provider, token, params, events, audio }) {
    if (provider !== "twilio") return Promise.reject(new BrowserPhoneLoadError());
    return startBrowserCall({ token, params, events, audio });
  },
  preload() {
    // Un fallimento qui non è un errore di nessuno: la chiamata riprova, e se non riesce lo dice lei.
    void loadVoiceSdk().catch(() => undefined);
  },
};
